'use client'

import React, { useRef, useState } from 'react'

import { Box, SystemStyleObject, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Tooltip } from '../Tooltip'
import {
  getUtilityIconColor,
  utilityButtonStyles,
} from './UtilityButton.styles'

export type UtilityButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'disabled' | 'onClick'
> & {
  icon: React.ReactElement<{ color?: string; size?: number }>
  label: string
  isSelected?: boolean
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  colorScheme?: ColorScheme
  /**
   * Fixed color applied to both the SVG `color` and `strokeColor` props on the cloned icon.
   * Both are set together because dark-package icons use `color` while legacy web-app icons
   * use `strokeColor` — the dual assignment ensures compatibility across both icon sources.
   * When provided, bypasses the automatic token-based color selection.
   */
  iconColor?: string
  stylesOverride?: SystemStyleObject
  /**
   * When provided, shows a dark-package Tooltip on hover.
   * No tooltip is shown by default.
   */
  tooltipLabel?: string
}

export const UtilityButton = React.forwardRef<
  HTMLButtonElement,
  UtilityButtonProps
>(
  (
    {
      icon,
      label,
      isSelected,
      disabled = false,
      onClick,
      onBlur,
      onFocus,
      colorScheme = 'dark',
      iconColor,
      stylesOverride,
      tooltipLabel,
      ...rest
    },
    ref
  ) => {
    const [isHovering, setIsHovering] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const wrapperRef = useRef<HTMLElement>(null)

    const resolvedIconColor =
      iconColor ??
      getUtilityIconColor(
        isSelected ?? false,
        isHovering || isFocused,
        colorScheme,
        disabled
      )

    const coloredIcon = React.cloneElement(
      icon as React.ReactElement<Record<string, unknown>>,
      { color: resolvedIconColor, strokeColor: resolvedIconColor }
    )

    const shouldShowTooltip =
      tooltipLabel != null &&
      isHovering &&
      !isSelected &&
      wrapperRef.current !== null

    return (
      <>
        <Box
          as='span'
          display='inline-block'
          onMouseEnter={() => {
            setIsHovering(true)
          }}
          onMouseLeave={() => {
            setIsHovering(false)
          }}
          ref={wrapperRef}
        >
          <chakra.button
            ref={ref}
            {...rest}
            aria-label={label}
            aria-pressed={isSelected}
            disabled={disabled}
            onBlur={(e) => {
              setIsFocused(false)
              onBlur?.(e)
            }}
            onClick={(e) => {
              ;(e.currentTarget as HTMLButtonElement).blur()
              onClick?.(e)
            }}
            onFocus={(e) => {
              setIsFocused(true)
              onFocus?.(e)
            }}
            type='button'
            {...utilityButtonStyles(isSelected ?? false, colorScheme)}
            {...stylesOverride}
          >
            {coloredIcon}
          </chakra.button>
        </Box>
        {shouldShowTooltip && (
          <Tooltip
            anchorEl={wrapperRef.current}
            colorScheme={colorScheme}
            label={tooltipLabel!}
            placement='bottom'
          />
        )}
      </>
    )
  }
)

UtilityButton.displayName = 'UtilityButton'
